"use client";

import {
  DocumentSnapshot,
  QueryDocumentSnapshot,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
  writeBatch,
  orderBy,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, storage } from "@/helpers/firebase/firebase";
import { Collections } from "@/helpers/utils/collections";
import { Company, Cart } from "@/models/marketPlace";
import { FormResponseStatus } from "@/models/form-types";
import Stripe from "stripe";
import { createStripeCheckoutSession } from "../utils/utility-functions";

let stripe: Stripe;
const apiKey = process.env.STRIPE_SECRET_KEY;
if (apiKey) {
  stripe = new Stripe(apiKey, { apiVersion: '2026-07-29.dahlia' });
}

const companyMap = (doc: QueryDocumentSnapshot): Company => {
  const data = doc.data() as Company;
  data.id = doc.id;
  return data;
};

const cartMap = (doc: DocumentSnapshot): Cart => {
  const data = doc.data() as Cart;
  data.id = doc.id;
  return data;
};

export async function getCompanies(): Promise<Company[]> {
  const companySnapshot = await getDocs(
    collection(db, Collections.MARKETPLACE)
  );
  return companySnapshot.docs.map((doc) => companyMap(doc));
}

export async function getCompanyInfoByDocumentId(
  id: string
): Promise<Company | undefined> {
  const companyDocRef = doc(db, `${Collections.MARKETPLACE}/${id}`);
  const companyDoc = await getDoc(companyDocRef);

  if (!companyDoc.exists()) return undefined;
  return companyMap(companyDoc);
}

export async function addDonationCartResponse(
  userId: string,
  cartId: string,
  cartResponse: Cart
) {
  await setDoc(
    doc(
      db,
      `${Collections.USERS}/${userId}/${Collections.DONATION_CART_RESPONSES}`,
      cartId
    ),
    {
      ...cartResponse,
    }
  );
  return true;
}

// Function to remove an item from the cart
export async function removeDonationCartItem(userId: string, cartId: string) {
  try {
    await deleteDoc(
      doc(
        db,
        `${Collections.USERS}/${userId}/${Collections.DONATION_CART_RESPONSES}`,
        cartId
      )
    );
    return true;
  } catch (error) {
    console.error("Error removing item: ", error);
  }
}

export async function getDonationCart(userId: string): Promise<Cart[]> {
  const cartSnapshot = await getDocs(
    collection(
      db,
      `${Collections.USERS}/${userId}/${Collections.DONATION_CART_RESPONSES}`
    )
  );
  const cartData = cartSnapshot.docs.map(cartMap);
  return cartData;
}

export async function deleteDonationCart(userId: string) {
  const collectionPath = `${Collections.USERS}/${userId}/${Collections.DONATION_CART_RESPONSES}`;
  const collectionRef = collection(db, collectionPath);
  const querySnapshot = await getDocs(collectionRef);

  const batch = writeBatch(db);
  querySnapshot.forEach((document) => {
    const documentRef = doc(db, collectionPath, document.id);
    batch.delete(documentRef);
  });

  await batch.commit();
}

const createLineItems = (cartData: Cart[], chargeAmount: number) => {
  const lineItems = cartData.map((item: Cart) => ({
    price_data: {
      currency: "cad",
      product_data: {
        name: item.companyName,
        images: [item.companyLogo],
        description: `Donation given for ${item.companyName}`,
        metadata: {
          ownerId: item.ownerId,
          productId: item.companyId,
        },
      },
      unit_amount: Number(item.donation) * 100,
    },
    quantity: 1,
  }));

  // Add the service fee as a separate line item
  lineItems.push({
    price_data: {
      currency: "cad",
      product_data: {
        name: "Service Fee",
        images: [], // Add images property here
        description: "Processing fee",
        metadata: {
          ownerId: "",
          productId: "",
        },
      },
      unit_amount: chargeAmount,
    },
    quantity: 1,
  });

  return lineItems;
};

export async function handleStripePayment(request: any, res: any) {
  const { data } = await request;
  const { cartData, donation, chargeAmount, cancelUrl, successUrl, customer } =
    data;
  if (!donation) {
    res.status(400).json({ error: "Amount is required" });
    return;
  }
  try {
    const lineItems = createLineItems(cartData, chargeAmount);

    // const session = await stripe.checkout.sessions.create({
    //   payment_method_types: ["card"],
    //   customer,
    //   line_items: lineItems,
    //   mode: "payment",
    //   cancel_url: cancelUrl,
    //   success_url: successUrl,
    //   metadata: {
    //     payment_type: "donation",
    //     // cartData: JSON.stringify(cartData)
    //   },
    // });

    return await createStripeCheckoutSession({
      lineItems,
      uid: customer,
      successUrl,
      cancelUrl,
      metadata: { payment_type: "donation" },
    });
  } catch (error) {
    console.error(
      "Error creating payment intent:",
      (error as Error).message as string
    );
    // res.status(500).json({ error: 'Internal Server Error', details: error && error?.message });
    res.status(500).json({ error: "Error checkout session" });
  }
}

export async function addMarketPlaceResponseToDb(
  userId: string,
  marketPlaceResponse: Company
) {
  await addDoc(collection(db, Collections.MARKETPLACE), {
    ...marketPlaceResponse,
    uid: userId,
    trending: true,
    timestamp: serverTimestamp(),
    donation: 0,
    status: FormResponseStatus.IN_REVIEW,
  });
  return true;
}

export async function getMyMarketPlace(userId: string): Promise<Company[]> {
  const mymarketPlaceSnapshot = await getDocs(
    query(
      collection(db, Collections.MARKETPLACE),
      where("uid", "==", userId),
      orderBy("timestamp", "desc"),
      limit(1)
    )
  ); 

  return mymarketPlaceSnapshot.docs.map((doc) => companyMap(doc));
}

export async function getExistingMarketPlace(
  userId: string,
  marketPlaceId: string
): Promise<Company | undefined> {
  const marketPlaceResponseDoc = await getDoc(
    doc(db, Collections.MARKETPLACE, marketPlaceId)
  );
  return marketPlaceResponseDoc.exists()
    ? (marketPlaceResponseDoc.data() as Company)
    : undefined;
}

export async function updateMyMarketPlaceToDb(
  userId: string,
  marketPlaceId: string,
  marketPlaceResponse: Company
): Promise<boolean> {
  try {
    await updateDoc(doc(db, Collections.MARKETPLACE, marketPlaceId), {
      ...marketPlaceResponse,
      uid: userId,
      trending: true,
      timestamp: serverTimestamp(),
    });
    return true;
  } catch (e) {
    return false;
  }
}
